// Projects and their workflows.
import { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Popconfirm, Segmented, Spin, Tag, Tooltip } from "antd";
import { FolderPlus, Plus, Trash2, Users, Workflow as WorkflowIcon } from "lucide-react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addNewProject,
  deleteProjectById,
  deleteWorkflow,
  getAccessibleProjects,
  getProjectWorkflows,
  getSharedWorkflows,
} from "../../api/workflowServices";
import { getOverLimitChecks } from "../../store/userSlice";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : "");
const personName = (user) => (user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "");

const WorkflowCard = ({ workflow, onDelete }) => (
  <div className="group relative flex flex-col rounded-xl border border-gray-200 bg-white p-4 hover:border-lightBlue hover:shadow-md transition">
    <Link to={`/portal/workflows/${workflow._id}`} className="absolute inset-0" aria-label={`Open ${workflow.name}`} />
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2">
        <WorkflowIcon size={18} className="text-lightBlue" />
        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2">{workflow.name}</h3>
      </div>
      {onDelete && (
        <Popconfirm title={`Delete “${workflow.name}”?`} description="Its version history is deleted too." onConfirm={() => onDelete(workflow)}>
          <button type="button" className="relative z-10 rounded p-1 text-gray-300 opacity-0 hover:bg-red-50 hover:text-red-500 group-hover:opacity-100">
            <Trash2 size={14} />
          </button>
        </Popconfirm>
      )}
    </div>
    {workflow.description && <p className="mt-1 text-xs text-gray-500 line-clamp-2">{workflow.description}</p>}
    <div className="mt-3 flex flex-wrap gap-1">
      <Tag>{workflow.sources.length} source{workflow.sources.length === 1 ? "" : "s"}</Tag>
      <Tag>{workflow.stepCount} step{workflow.stepCount === 1 ? "" : "s"}</Tag>
      <Tag>{workflow.outputs.length} output{workflow.outputs.length === 1 ? "" : "s"}</Tag>
      {workflow.visibility === "company" && (
        <Tag color="blue" icon={<Users size={10} className="mr-1 inline" />}>
          Shared
        </Tag>
      )}
    </div>
    <div className="mt-auto pt-3 text-[11px] text-gray-400">
      v{workflow.version} · updated {formatDate(workflow.updatedAt)}
      {workflow.updatedBy ? ` by ${personName(workflow.updatedBy)}` : ""}
    </div>
  </div>
);

const WorkflowsHome = () => {
  const { user } = useSelector((state) => state.user);
  const limits = useSelector(getOverLimitChecks);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState(null);
  const [workflows, setWorkflows] = useState(null);
  const [tab, setTab] = useState("project");
  const [newProject, setNewProject] = useState(null); // project name while the dialog is open
  const projectId = searchParams.get("project") || projects?.[0]?._id;
  const project = projects?.find((p) => p._id === projectId);
  const canCreateProjects = user?.role === "super_admin" || user?.permissions?.includes("Template Manager");
  const assignment = user?.projectsAssigned?.find((a) => (a.project?._id ?? a.project) === projectId);
  const canCreateWorkflows = user?.role === "super_admin" || assignment?.accessControls?.create;

  const loadProjects = useCallback(() => {
    getAccessibleProjects()
      .then((response) => setProjects(response.data || []))
      .catch(() => setProjects([]));
  }, []);
  useEffect(loadProjects, [loadProjects]);

  useEffect(() => {
    setWorkflows(null);
    const request = tab === "shared" ? getSharedWorkflows() : projectId ? getProjectWorkflows(projectId) : null;
    if (!request) return setWorkflows([]);
    request.then((response) => setWorkflows(response.data || [])).catch(() => setWorkflows([]));
  }, [tab, projectId]);

  const createProject = async () => {
    try {
      const response = await addNewProject(newProject.trim());
      setNewProject(null);
      loadProjects();
      setSearchParams({ project: response.data._id });
    } catch (error) {
      toast.error(error?.message || error?.error || "Could not create the project");
    }
  };

  const removeProject = async (target) => {
    try {
      await deleteProjectById(target._id);
      toast.success("Project deleted");
      setSearchParams({});
      loadProjects();
    } catch (error) {
      toast.error(error?.message || "Could not delete the project");
    }
  };

  const removeWorkflow = async (workflow) => {
    try {
      await deleteWorkflow(workflow._id);
      setWorkflows((current) => current.filter((w) => w._id !== workflow._id));
      toast.success("Workflow deleted");
    } catch (error) {
      toast.error(error?.message || "Could not delete the workflow");
    }
  };

  if (!projects) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="flex h-full gap-4 overflow-hidden p-2">
      <aside className="flex w-64 shrink-0 flex-col rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-semibold text-gray-800">Projects</span>
          {canCreateProjects && (
            <Tooltip title="New project">
              <Button size="small" type="text" icon={<FolderPlus size={16} />} onClick={() => setNewProject("")} />
            </Tooltip>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {projects.length === 0 && <p className="p-2 text-xs text-gray-500">No projects yet.</p>}
          {projects.map((item) => (
            <div
              key={item._id}
              onClick={() => {
                setTab("project");
                setSearchParams({ project: item._id });
              }}
              className={`group flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-sm ${
                item._id === projectId && tab === "project" ? "bg-blue-50 font-semibold text-lightBlue" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="truncate">{item.projectName}</span>
              {(user?.role === "super_admin" || item.createdBy?._id === user?._id) && (
                <Popconfirm title={`Delete project “${item.projectName}”?`} description="All its workflows are deleted too." onConfirm={() => removeProject(item)}>
                  <button type="button" onClick={(e) => e.stopPropagation()} className="text-gray-300 opacity-0 hover:text-red-500 group-hover:opacity-100">
                    <Trash2 size={13} />
                  </button>
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Segmented
            value={tab}
            onChange={setTab}
            options={[
              { value: "project", label: project ? project.projectName : "Project" },
              { value: "shared", label: "Shared with my organisation" },
            ]}
          />
          <div className="ml-auto flex gap-2">
            {tab === "project" && project && canCreateWorkflows && (
              <Tooltip title={limits.workflowsExceeded ? "Your plan's workflow limit is reached" : ""}>
                <Button
                  type="primary"
                  className="!bg-lightBlue"
                  icon={<Plus size={14} />}
                  disabled={limits.workflowsExceeded}
                  onClick={() => navigate(`/portal/workflows/new?project=${project._id}`)}
                >
                  New workflow
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!workflows ? (
            <div className="py-16 text-center">
              <Spin />
            </div>
          ) : workflows.length === 0 ? (
            <Empty
              className="py-16"
              description={
                tab === "shared"
                  ? "Nothing has been shared with your organisation yet."
                  : projects.length
                    ? "No workflows in this project yet."
                    : "Create a project to start building workflows."
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 xxl:grid-cols-4">
              {workflows.map((workflow) => (
                <WorkflowCard key={workflow._id} workflow={workflow} onDelete={tab === "project" ? removeWorkflow : undefined} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Modal
        open={newProject !== null}
        title="New project"
        okText="Create"
        okButtonProps={{ disabled: !newProject?.trim() }}
        onOk={createProject}
        onCancel={() => setNewProject(null)}
      >
        <Input autoFocus placeholder="Project name" value={newProject ?? ""} onChange={(e) => setNewProject(e.target.value)} onPressEnter={() => newProject?.trim() && createProject()} />
      </Modal>
    </div>
  );
};

export default WorkflowsHome;
